import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { IconButton, Paper, Popper, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import * as React from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { messagingActions } from '../../reducers/messagingSlice';
import { selectIsPlaying } from '../../selectors/message';
import { selectFeatureFlagHitl3 } from '../../redux/features/config/config.selector';
import { audioPlayer } from '../../utils/asrcAudio';
import Colors from '../../utils/color';
import { HeaderVolumeSlider } from '../MainPageLayout/HeaderVolumeSlider';

const useStyles = makeStyles((theme: Theme) => ({
  iconContainer: {
    width: '32px',
    height: '100%',
    padding: '0px !important',
    display: 'flex',
    flexDirection: 'column',
    margin: '0px !important',
    flexShrink: 0,
  },
  icon: {
    color: Colors.dim,
    width: '100% !important',
    height: '32px !important',
    padding: '4px',
    paddingRight: '4px',
    borderRadius: '4px',
  },
  active: {
    color: Colors.cerulean,
    width: '100% !important',
    height: '42px !important',
    padding: '2px',
    paddingRight: '4px',
    borderRadius: '4px',
  },
}));

export const Player = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const isPlaying = useAppSelector(selectIsPlaying);
  const isHitl3 = useAppSelector(selectFeatureFlagHitl3);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const closeTimer = React.useRef<ReturnType<typeof setTimeout>>();
  // Mirror of the player's mute state so the icon re-renders; the level and the
  // mute flag themselves live on the audioPlayer singleton (HITL-1472).
  const [isMuted, setIsMuted] = React.useState(audioPlayer.isMuted);

  // Playback toggling tears the AudioContext down and builds a new one. The
  // player carries the level and mute flag across that, so re-sync the icon
  // rather than letting it drift back to its initial value.
  React.useEffect(() => {
    setIsMuted(audioPlayer.isMuted);
  }, [isPlaying]);

  const toggleAudio = () => {
    dispatch(messagingActions.setIsPlaying(!isPlaying));
  };

  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };
  // Volume only matters while audio is playing — reveal the slider on hover.
  const openPopper = (event: React.MouseEvent<HTMLElement>) => {
    if (!isPlaying) return;
    cancelClose();
    setAnchorEl(event.currentTarget);
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setAnchorEl(null), 200);
  };

  const open = Boolean(anchorEl) && isPlaying;

  // OFF (HITL 3.0 disabled): the original simple play/pause toggle. The inline
  // transcript VolumeSlider (Audio/VolumeSlider) carries volume in this mode.
  if (!isHitl3) {
    return (
      <IconButton
        onClick={() => toggleAudio()}
        className={classes.iconContainer}
      >
        {
          <>
            <VolumeUpIcon
              fontSize="small"
              className={isPlaying ? classes.active : classes.icon}
            />
          </>
        }
      </IconButton>
    );
  }

  // ON (HITL 3.0): the header carries volume via a hover popover; the transcript
  // card no longer renders an inline volume control.
  return (
    <>
      <div onMouseEnter={openPopper} onMouseLeave={scheduleClose}>
        <IconButton onClick={toggleAudio} className={classes.iconContainer}>
          {isPlaying && isMuted ? (
            // Muted while playing → show "0 sound" (off) icon.
            <VolumeOffIcon fontSize="small" className={classes.icon} />
          ) : (
            <VolumeUpIcon
              fontSize="small"
              className={isPlaying ? classes.active : classes.icon}
            />
          )}
        </IconButton>
      </div>
      {/* Popper (not Popover) — no backdrop/modal, so hover isn't interrupted. */}
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom"
        // One above the default modal zIndex (1300) so the volume popover sits
        // over surrounding chrome. Inline (not makeStyles) so the OFF Player's
        // style rules stay byte-identical to the original.
        style={{ zIndex: 1301 }}
      >
        <Paper
          elevation={3}
          // The popover portals out of the header to document.body, so tag it
          // as header chrome — otherwise dragging the volume slider counts as
          // an order interaction and trips the "take over this order?" prompt
          // (AIStatusBar excludes [data-header="true"] from that detection).
          data-header="true"
          sx={{ width: 220, py: 1, px: 1.5 }}
          onMouseEnter={cancelClose}
          // Don't close while a button is held (dragging the slider past the
          // paper edge would otherwise dismiss the popover mid-drag).
          onMouseLeave={(e) => {
            if (e.buttons === 0) scheduleClose();
          }}
        >
          <HeaderVolumeSlider isMuted={isMuted} onMutedChange={setIsMuted} />
        </Paper>
      </Popper>
    </>
  );
};
